import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MODULES } from "@/config/modules";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, RefreshCw, Search, Pencil, Trash2, Sparkles, X, FileSpreadsheet, Download } from "lucide-react";
import RecordDialog from "@/components/RecordDialog";
import StatusPill from "@/components/StatusPill";
import BulkImportDialog from "@/components/BulkImportDialog";
import FileAttachments from "@/components/FileAttachments";
import PersonAvatar from "@/components/PersonAvatar";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

function AiPanel({ open, onClose, record, module }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !record) return;
    setPlan(null);
    setError("");
    setLoading(true);

    const contextLines = [];
    for (const f of module.fields) {
      if (record[f.name]) contextLines.push(`${f.label}: ${record[f.name]}`);
    }
    const context = contextLines.join("\n");

    api.post("/ai/action-plan", { context, module: module.key })
      .then((r) => setPlan(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, record, module]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white" data-testid="ai-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-600" /> AI-Generated Action Plan
          </DialogTitle>
          <DialogDescription className="sr-only">
            SMART action plan drafted by Claude Sonnet 4.5 for this {module.label} record.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-sm text-slate-500">Drafting action plan with Claude Sonnet 4.5…</div>}
        {error && <div className="py-4 text-sm text-red-600" data-testid="ai-plan-error">{error}</div>}
        {plan && (
          <div className="space-y-4 text-sm">
            {plan.summary && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Summary</div>
                <p className="text-slate-800">{plan.summary}</p>
              </div>
            )}
            {plan.root_cause && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Likely Root Cause</div>
                <p className="text-slate-800">{plan.root_cause}</p>
              </div>
            )}
            {Array.isArray(plan.actions) && plan.actions.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Actions</div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Action</th><th className="w-28">Owner</th><th className="w-20">Due (d)</th><th className="w-20">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.actions.map((a, i) => (
                      <tr key={i}>
                        <td>{a.action}</td>
                        <td className="capitalize">{a.owner_role?.replace(/_/g, " ")}</td>
                        <td className="tabular-nums">{a.due_days}</td>
                        <td><StatusPill value={a.priority} kind="severity" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {Array.isArray(plan.preventive_measures) && plan.preventive_measures.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Preventive Measures</div>
                <ul className="list-disc list-inside space-y-0.5 text-slate-800">
                  {plan.preventive_measures.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            {plan.regulatory_notes && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Regulatory Notes</div>
                <p className="text-slate-800">{plan.regulatory_notes}</p>
              </div>
            )}
            {plan.raw && (
              <pre className="bg-slate-50 border border-slate-200 p-2 text-xs whitespace-pre-wrap">{plan.raw}</pre>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RecordsPage() {
  // Accept either :moduleKey (legacy dedicated records route) or :orgSlug
  // (the path-based tenant-routing catchall that falls back to RecordsPage
  // when the segment is not a known active tenant). Both point at the same
  // module key, e.g. /maintenance or /medication-errors.
  const params = useParams();
  const moduleKey = params.moduleKey || params.orgSlug;
  const module = MODULES[moduleKey];
  const { user } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiRecord, setAiRecord] = useState(null);

  const load = async () => {
    setLoading(true);
    setRows([]);
    try {
      const r = await api.get(`/${moduleKey}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setSelectedId(null); load(); }, [moduleKey]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  const selected = rows.find((r) => r.id === selectedId);
  const canDelete = user?.role === "admin" || user?.role === "manager";

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await api.put(`/${moduleKey}/${editing.id}`, { data: values });
        toast.success("Record updated");
      } else {
        await api.post(`/${moduleKey}`, { data: values });
        toast.success("Record created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      await api.delete(`/${moduleKey}/${id}`);
      toast.success("Deleted");
      if (selectedId === id) setSelectedId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!module) return <div className="p-8 text-sm text-red-600">Unknown module: {moduleKey}</div>;

  const bulkEnabled = ["staff", "clients", "training"].includes(moduleKey);

  const handleExportPdf = async () => {
    try {
      await downloadBlob(`/reports/module/${moduleKey}`, `ayobell-${moduleKey}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Toolbar */}
      <div className="min-h-14 border-b border-slate-200 bg-white px-4 lg:px-6 py-2 lg:py-0 flex items-center justify-between shrink-0 gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 hidden lg:block">{module.group}</div>
          <h1 className="font-heading text-sm lg:text-base font-semibold truncate" data-testid="page-title">{module.short}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px] lg:flex-none lg:w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid="records-search"
              className="w-full h-10 lg:h-9 pl-8 text-sm bg-white"
            />
          </div>
          <Button
            variant="outline" size="sm" onClick={load}
            data-testid="btn-refresh" className="h-10 lg:h-9 text-slate-700"
          ><RefreshCw className="w-3.5 h-3.5 lg:mr-1.5" /> <span className="hidden lg:inline">Refresh</span></Button>
          {bulkEnabled && (
            <Button
              variant="outline" size="sm"
              onClick={() => setBulkOpen(true)}
              data-testid="btn-bulk-import"
              className="h-10 lg:h-9 text-slate-700 hidden lg:inline-flex"
            ><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Import CSV</Button>
          )}
          <Button
            variant="outline" size="sm"
            onClick={handleExportPdf}
            data-testid="btn-export-pdf"
            className="h-10 lg:h-9 text-slate-700 hidden lg:inline-flex"
          ><Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF</Button>
          <Button
            size="sm"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="btn-new-record"
            className="h-11 lg:h-9 bg-sky-700 hover:bg-sky-800"
          ><Plus className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">New Record</span><span className="sm:hidden">New</span></Button>
        </div>
      </div>

      {/* Table + detail */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <div className="flex-1 min-w-0 overflow-auto bg-white">
          {loading ? (
            <div className="p-8 text-sm text-slate-500" data-testid="records-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <div className="text-sm text-slate-500 mb-3">No records yet.</div>
              <Button
                size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}
                data-testid="btn-empty-new" className="bg-sky-700 hover:bg-sky-800"
              ><Plus className="w-3.5 h-3.5 mr-1.5" /> Add the first {module.short.toLowerCase()} record</Button>
            </div>
          ) : (
            <>
            {/* Mobile / tablet: card list */}
            <ul className="lg:hidden space-y-2 p-4" data-testid="records-cards">
              {filtered.map((r) => {
                const primary = r[module.fields[0].name] || r.title || "Record";
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => moduleKey === "clients" ? navigate(`/clients/${r.id}`) : setSelectedId(r.id)}
                      data-testid={`mobile-row-${r.id}`}
                      className="block w-full text-left bg-white border border-slate-200 rounded-lg p-4 min-h-[64px] active:bg-slate-50"
                    >
                      <div className="font-medium text-slate-900 text-[15px] break-words">{primary}</div>
                      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                        {module.columns.slice(0, 4).map((c) => {
                          const v = r[c.key];
                          if (v == null || v === "") return null;
                          return (
                            <React.Fragment key={c.key}>
                              <dt className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold self-center">{c.label}</dt>
                              <dd className={`text-slate-700 break-words ${c.numeric ? "tabular-nums text-right" : ""}`}>
                                {c.status ? <StatusPill value={v} kind={c.status} /> : `${v}${c.suffix || ""}`}
                              </dd>
                            </React.Fragment>
                          );
                        })}
                      </dl>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Desktop: unchanged data-table */}
            <table className="data-table hidden lg:table" data-testid="records-table">
              <thead>
                <tr>
                  <th className="row-num">#</th>
                  {moduleKey === "clients" && <th className="w-12"></th>}
                  {module.columns.map((c) => (
                    <th key={c.key} className={c.numeric ? "text-right" : ""}>{c.label}</th>
                  ))}
                  <th className="w-24 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r.id}
                    data-testid={`row-${r.id}`}
                    onClick={() => {
                      if (moduleKey === "clients") navigate(`/clients/${r.id}`);
                      else setSelectedId(r.id);
                    }}
                    className={selectedId === r.id ? "bg-sky-50" : ""}
                  >
                    <td className="row-num">{i + 1}</td>
                    {moduleKey === "clients" && (
                      <td><PersonAvatar person={r} name={r.full_name} size="sm" testid={`avatar-client-${r.id}`} /></td>
                    )}
                    {module.columns.map((c) => {
                      const v = r[c.key];
                      if (c.status) return <td key={c.key}><StatusPill value={v} kind={c.status} /></td>;
                      if (c.render === "pills") {
                        const items = asArray(v);
                        return (
                          <td key={c.key}>
                            {items.length === 0 ? <span className="text-slate-300">—</span> : (
                              <div className="flex flex-wrap gap-1">
                                {items.slice(0, 3).map((it) => (
                                  <span key={it} className="inline-flex px-1.5 py-0.5 text-[11px] rounded-full bg-sky-50 text-sky-700 border border-sky-200">{it}</span>
                                ))}
                                {items.length > 3 && <span className="text-[11px] text-slate-500">+{items.length - 3}</span>}
                              </div>
                            )}
                          </td>
                        );
                      }
                      if (c.render === "allergy_pills") {
                        const items = asArray(v);
                        return (
                          <td key={c.key}>
                            {items.length === 0 ? <span className="text-slate-300">—</span> : (
                              <div className="flex flex-wrap gap-1">
                                {items.slice(0, 3).map((it) => (
                                  <span
                                    key={it}
                                    className={`inline-flex px-1.5 py-0.5 text-[11px] rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(it)]}`}
                                  >{it}</span>
                                ))}
                                {items.length > 3 && <span className="text-[11px] text-slate-500">+{items.length - 3}</span>}
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} className={c.numeric ? "text-right tabular-nums" : ""}>
                          {v != null && v !== "" ? `${v}${c.suffix || ""}` : <span className="text-slate-300">—</span>}
                        </td>
                      );
                    })}
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditing(r); setDialogOpen(true); }}
                        className="p-1 text-slate-500 hover:text-sky-700"
                        data-testid={`btn-edit-${r.id}`} title="Edit"
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                          className="p-1 text-slate-500 hover:text-red-600 ml-0.5"
                          data-testid={`btn-delete-${r.id}`} title="Delete"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>

        {/* Detail pane */}
        {selected && (
          <aside className="w-96 shrink-0 border-l border-slate-200 bg-slate-50 overflow-y-auto" data-testid="detail-pane">
            <div className="p-4 border-b border-slate-200 bg-white flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Record Details</div>
                <div className="font-heading text-sm font-semibold text-slate-900 mt-0.5">
                  {selected[module.fields[0].name] || selected.title || "Record"}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 mono">{selected.id}</div>
              </div>
              <button onClick={() => setSelectedId(null)} className="text-slate-400 hover:text-slate-700" data-testid="btn-close-detail">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {module.fields.map((f) => {
                const v = selected[f.name];
                let content;
                if (Array.isArray(v)) {
                  content = v.length ? (
                    <div className="flex flex-wrap gap-1">
                      {v.map((x) => <span key={x} className="pill pill-info">{x}</span>)}
                    </div>
                  ) : <span className="text-slate-300">—</span>;
                } else {
                  content = v ? <div className="whitespace-pre-wrap">{v}</div> : <span className="text-slate-300">—</span>;
                }
                return (
                  <div key={f.name}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{f.label}</div>
                    <div className="text-sm text-slate-800">{content}</div>
                  </div>
                );
              })}
              <div className="pt-2 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Created</div>
                <div className="text-xs text-slate-600">{selected.created_at?.slice(0, 19).replace("T", " ")} · {selected.created_by_name}</div>
              </div>

              <FileAttachments module={moduleKey} recordId={selected.id} />

              {module.aiEnabled && (
                <Button
                  size="sm" className="w-full bg-slate-900 hover:bg-slate-800"
                  onClick={() => { setAiRecord(selected); setAiOpen(true); }}
                  data-testid="btn-generate-ai-plan"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate AI Action Plan
                </Button>
              )}
            </div>
          </aside>
        )}
      </div>

      <RecordDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        module={module} record={editing}
        onSubmit={handleSubmit}
      />
      <BulkImportDialog
        open={bulkOpen} onOpenChange={setBulkOpen}
        module={module} onDone={load}
      />
      <AiPanel open={aiOpen} onClose={setAiOpen} record={aiRecord} module={module} />
    </div>
  );
}
